// src/components/Configure.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import './Configure.css';

function Configure() {
  const navigate = useNavigate();
  const location = useLocation();
  // Retrieve selected model from CreateModel; default to "GPT-4o" if not provided.
  const { selectedModel } = location.state || { selectedModel: "GPT-4o" };

  // State for configuration fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [file, setFile] = useState(null);

  // Capabilities with placeholder descriptions
  const capabilitiesList = [
    { label: 'Writing', tooltip: 'Enhance writing with advanced prompts.' },
    { label: 'Code Interpreter', tooltip: 'Interpret and analyze code in various languages.' },
    { label: 'Data Analysis', tooltip: 'Perform data queries, summaries, and more.' },
    { label: 'Education', tooltip: 'Explain topics in an educational manner.' },
    { label: 'Web Search', tooltip: 'Find and retrieve up-to-date information from the web.' }
  ];

  // Track selected capabilities in a Set
  const [selectedCapabilities, setSelectedCapabilities] = useState(new Set());
  const handleCheckboxChange = (capability) => {
    const updatedSet = new Set(selectedCapabilities);
    if (updatedSet.has(capability)) {
      updatedSet.delete(capability);
    } else {
      updatedSet.add(capability);
    }
    setSelectedCapabilities(updatedSet);
  };

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    }
  };

  /* Chat interface state for the Preview/Test Panel */
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  // State to store applied configuration (as a string) for testing
  const [appliedTestConfig, setAppliedTestConfig] = useState("");

  // "Save to Test" button saves the current configuration details for use in testing
  // and displays a confirmation message.
  const handleSaveToTest = () => {
    const configDetails = `Name: ${name}\nDescription: ${description}\nInstructions: ${instructions}\nCapabilities: ${Array.from(selectedCapabilities).join(', ')}`;
    setAppliedTestConfig(configDetails);
    alert("✅ Configuration has been updated!");
  };

  // Handle sending a chat message and receiving a response from OpenAI Chat API
  const handleSendMessage = async () => {
    if (chatInput.trim() === "") return;

    // Build conversation for API call:
    // If a configuration has been saved, prepend it as a system message.
    const conversation = [];
    if (appliedTestConfig) {
      conversation.push({ role: "system", content: appliedTestConfig });
    }
    conversation.push(...chatMessages);
    conversation.push({ role: "user", content: chatInput });

    // Update the UI chat history with the new user message.
    setChatMessages([...chatMessages, { role: "user", content: chatInput }]);
    setChatInput("");

    // Determine model: if selectedModel is "GPT-4o", use the full model identifier.
    const modelToUse = selectedModel === "GPT-4o" ? "gpt-4o-2024-08-06" : selectedModel;
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    if (!apiKey) {
      alert("OpenAI API key is not set.");
      return;
    }
    try {
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: modelToUse,
          messages: conversation,
          max_tokens: 150,
          temperature: 0.7
        })
      });
      const data = await response.json();
      if (data.error) {
        setChatMessages(prev => [...prev, { role: "assistant", content: "Error: " + data.error.message }]);
      } else {
        const assistantMessage = data.choices[0].message.content;
        setChatMessages(prev => [...prev, { role: "assistant", content: assistantMessage }]);
      }
    } catch (error) {
      console.error("Error calling OpenAI Chat API:", error);
      setChatMessages(prev => [...prev, { role: "assistant", content: "Error: " + error.message }]);
    }
  };

  // Save configuration to Firestore and redirect to My Agents screen
  const handleSaveAndCreate = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert("You must be logged in to create a custom AI.");
      navigate('/login');
      return;
    }
    try {
      await addDoc(collection(db, 'agents'), {
        owner: currentUser.uid,
        model: selectedModel,
        name,
        description,
        instructions,
        capabilities: Array.from(selectedCapabilities),
        createdAt: serverTimestamp(),
        share: false
      });
      alert("Custom AI created successfully!");
      navigate('/myagents');
    } catch (error) {
      console.error("Error creating custom AI:", error);
      alert("Error creating custom AI: " + error.message);
    }
  };

  return (
    <div className="configure-container">
      {/* Left: Configuration Panel */}
      <div className="configure-left">
        <h2>Create Your Custom AI</h2>
        
        <div className="form-group">
          <label htmlFor="name">Name your GPT</label>
          <input
            id="name"
            type="text"
            placeholder="e.g., My Awesome GPT"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <input
            id="description"
            type="text"
            placeholder="Add a short description about what this GPT does"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="instructions">Instructions</label>
          <textarea
            id="instructions"
            placeholder="What does this GPT do? How does it behave? What should it avoid doing?"
            rows={4}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Knowledge</label>
          <p className="knowledge-subtext">
            Upload files under Knowledge, so your GPT may include file contents during conversations.
          </p>
          <div className="file-upload">
            <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} />
          </div>
        </div>

        <div className="form-group">
          <label>Capabilities</label>
          <div className="capabilities-list">
            {capabilitiesList.map((cap) => (
              <div className="capability-item" key={cap.label}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedCapabilities.has(cap.label)}
                    onChange={() => handleCheckboxChange(cap.label)}
                  />
                  <span>{cap.label}</span>
                </label>
                <div className="tooltip-container">
                  <span className="tooltip-icon">?</span>
                  <div className="tooltip-text">{cap.tooltip}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* "Save to Test" button applies the configuration for testing */}
        <button className="save-test-button" onClick={handleSaveToTest}>
          Save to Test
        </button>
        {/* "Save and Create" button stores the custom AI in Firestore */}
        <button className="save-button" onClick={handleSaveAndCreate}>
          Save and Create
        </button>
      </div>

      {/* Right: Chat/Preview and Test Panel */}
      <div className="configure-right">
        <h3>Chat Preview: {selectedModel}</h3>
        <div className="chat-container">
          {chatMessages.map((msg, index) => (
            <div key={index} className={`chat-message ${msg.role}`}>
              <div className="chat-message-content">{msg.content}</div>
            </div>
          ))}
        </div>
        <div className="chat-input-container">
          <input
            type="text"
            placeholder="Type your message..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
          />
          <button className="send-button" onClick={handleSendMessage}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

export default Configure;
